import {observable, computed, action} from 'mobx'
import {Stores} from '.'
import {VAALIKONE_TOKEN_KEY} from './config'
import axios from 'axios'
import {AlmaAccountCore} from '@almamedia/alma-account-core'
import {almaTunnusConfig, almaTunnusUtils, logoutUrl} from '../almaTunnusConfig'
import {validateLoginData} from './api/auth'

export type LOGIN_STATUS =
  | 'loading'
  | 'unauthorized'
  | 'tunnus_redirect'
  | 'tunnus_redirect_done'
  | 'logged_in'
  | 'election_closed'
  | 'valid_data'
  | 'invalid_data'

export class AuthStore {
  private core: AlmaAccountCore | undefined

  constructor(Stores: Stores) {
    this.stores = Stores
  }
  @observable
  stores?: Stores = undefined
  @observable
  status: LOGIN_STATUS = 'unauthorized'
  @observable
  electionId?: number = undefined
  @observable
  @action
  setAuthHeader(token: string) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`
  }
  @action
  setToken(token: string | null | undefined) {
    if (!token) throw new Error('No token.')
    sessionStorage.setItem(VAALIKONE_TOKEN_KEY, token)
    this.setAuthHeader(token)
  }

  @action
  async validateLoginData(decodedDataString: string): Promise<void> {
    try {
      const {data} = await validateLoginData(decodedDataString)

      if (!data?.party_id || !data?.election_id) {
        throw new Error('Invalid login data')
      }

      const {election_id, party_id} = data
      sessionStorage.setItem('party_id', party_id)
      sessionStorage.setItem('election_id', election_id)
      this.setStatus('valid_data')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const {data} = error.response
        if (data?.election_open === false) {
          this.setStatus('election_closed')
        } else {
          this.setStatus('invalid_data')
        }
      }
    }
  }
  @action
  tunnusLogin = async () => {
    this.setStatus('tunnus_redirect')
    await this.core?.login({
      authorizationParams: {
        lang: 'fi',
        theme: 'light',
        source: 'login-button',
        connection: 'signicat',
        prompt: 'login',
        max_age: '0',
        ui_locales: 'fi'
      }
    })
  }

  @action
  tunnusLoginResultHandler = async () => {
    try {
      const query = window.location.search
      const shouldParseResult = query.includes('code=') && query.includes('state=')

      if (this.core && shouldParseResult) {
        const result = (await this.core.handleAuthentication()) as {appState?: any}
        const isAuthenticated = await this.core.isAuthenticated()

        if (!isAuthenticated || !result) {
          return this.setStatus('unauthorized')
        }

        const user = await this.core.getUserInfo()
        const token = await this.core.getAccessToken()

        if (!user || !token) {
          return this.setStatus('unauthorized')
        }

        const pId = sessionStorage.getItem('party_id')
        const eId = sessionStorage.getItem('election_id')

        if (!pId || !eId) {
          return this.setStatus('unauthorized')
        }

        const party_id = Number(pId)
        const election_id = Number(eId)

        const candidate = {
          tunnus_id: await almaTunnusUtils.formUniqueIdFromInputString(user.sub!),
          first_name: user.given_name!.split(' ')[0],
          given_name: user.given_name!,
          last_name: user.family_name!,
          birthday: user['https://almamedia.net/birthdate'],
          email: user.email!,
          status: 'ACTIVE',
          party_id,
          election_id
        }

        this.stores?.candidateStore.setTunnusCandidate(candidate)
        this.setStatus('tunnus_redirect_done')
      }
      this.setStatus('unauthorized')
      return null
    } catch (e) {
      this.setStatus('unauthorized')
      console.log('Error parsing redirect:', e) // eslint-disable-line no-console
    }
  }

  @action
  init = async () => {
    const core = AlmaAccountCore.getInstance({
      disableSentry: true,
      // @ts-expect-error - disableCMPHandler is not a valid property in the InstanceConfig type
      disableCMPHandler: true
    })
    await core?.init(almaTunnusConfig)
    this.core = core
  }

  @action
  setupTunnusTokenRefreshInterceptor() {
    axios.interceptors.request.use(async config => {
      try {
        const isAuthenticated = await this.core?.isAuthenticated()

        if (isAuthenticated) {
          const token = await this.core?.getAccessToken()
          if (!token) {
            this.tunnusLogout()
          } else {
            config.headers['Authorization'] = `Bearer ${token}`
          }
        }
        return Promise.resolve(config)
      } catch (error) {
        this.tunnusLogout()
        return config
      }
    })
  }

  @action
  setStatus(status: LOGIN_STATUS) {
    this.status = status
  }

  @computed
  get isAuthenticated() {
    return this.status === 'logged_in' || this.status === 'tunnus_redirect_done'
  }

  @action
  tunnusLogout = async () => {
    sessionStorage.removeItem(VAALIKONE_TOKEN_KEY)
    delete axios.defaults.headers.common.Authorization
    this.setStatus('unauthorized')
    await this.core?.logout(logoutUrl, true)
  }

  @action
  getCore = () => {
    return this.core
  }
}
